import React, {
  useEffect,
  useState,
  useRef,
} from "react";

import { useParams } from "react-router-dom";

import { motion } from "framer-motion";

import { connectSocket } from "../socket";

import {
  fetchWithToken,
  API_BASE,
} from "../api/api";

import VideoCall from "../features/calls/VideoCall";
import VoiceRecorder from "../components/VoiceRecorder";
import VoiceCall from "../features/calls/VoiceCall";
import SponsoredAd from "../components/SponsoredAd";
import DateDivider from "../components/DateDivider";
import { formatMessageDate } from "../utils/dateHelpers";
import useWebRTC from "../features/calls/useWebRTC";
import CallScreen from "../features/calls/CallScreen";
import { useCall } from "../context/CallContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";




const defaultProfile =
  "https://afribook-backend.onrender.com/uploads/profiles/default-profile.png";

/* AUDIO MESSAGE COMPONENT */
const AudioMessage = ({ src, isMe, onDelete }) => {
  const audioRef = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!audio.paused) {
      audio.pause();
      return;
    }

    try {
      setLoading(true);
      await audio.play();
    } catch (err) {
      console.log("Audio play error:", err);
      setLoading(false);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;

    const newTime = Number(event.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  return (
    <div
      className={`flex items-center gap-3 mt-2 w-full min-w-0 rounded-full px-3 py-2 ${
        isMe
          ? "bg-gradient-to-r from-green-500 to-green-700 text-white"
          : "bg-green-100 text-gray-800"
      }`}
    >
      <button
        type="button"
        onClick={togglePlay}
        disabled={loading}
        aria-label={playing ? "Pause voice note" : "Play voice note"}
        className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm transition ${
          isMe
            ? "bg-white/20 text-white"
            : "bg-white text-green-700"
        }`}
      >
        {loading ? "…" : playing ? "Ⅱ" : "▶"}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs font-medium">Voice note</span>
          <span className="text-[11px] tabular-nums opacity-80">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={Math.min(currentTime, duration || 0)}
          onChange={handleSeek}
          disabled={!duration}
          aria-label="Seek voice note"
          className="w-full h-1.5 block cursor-pointer accent-green-600 disabled:cursor-not-allowed"
        />
      </div>

      {isMe && (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete voice note"
          className="shrink-0 text-sm text-red-200 hover:text-white"
        >
          🗑
        </button>
      )}

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration || 0);
        }}
        onDurationChange={(event) => {
          setDuration(event.currentTarget.duration || 0);
        }}
        onTimeUpdate={(event) => {
          setCurrentTime(event.currentTarget.currentTime || 0);
        }}
        onPlay={() => {
          setPlaying(true);
          setLoading(false);
        }}
        onPause={() => {
          setPlaying(false);
          setLoading(false);
        }}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
        }}
        onError={() => {
          setPlaying(false);
          setLoading(false);
          console.log("Unable to load voice note:", src);
        }}
        className="hidden"
      />
    </div>
  );
};

const Messages = () => {
const navigate = useNavigate();
  const socketRef = useRef(null);

  const messagesEndRef = useRef(null);

  const currentUser =
    localStorage.getItem("userId");

  const token =
    localStorage.getItem("token");

  const { id } = useParams();

  const [friends, setFriends] =
    useState([]);

  const [selectedUser, setSelectedUser] =
    useState(null);

  const [messages, setMessages] =
    useState([]);

  const [text, setText] =
    useState("");

  const [media, setMedia] =
    useState(null);

  const [uploading, setUploading] =
    useState(false);

  const [showCall, setShowCall] =
    useState(false);

const [isOutgoingCall, setIsOutgoingCall] =
  useState(false);

  

const [editText, setEditText] = useState("");
const [editingMessageId, setEditingMessageId] =
  useState(null);
const [openMenuId, setOpenMenuId] = useState(null);



  const [showSidebar, setShowSidebar] =
    useState(false);

const [onlineUsers, setOnlineUsers] = useState([]);


const menuRef = useRef(null);



const startEditMessage = (msg) => {
  setEditingMessageId(msg._id);
  setEditText(msg.text || "");
};


const webRTC = useWebRTC({
  currentUser,
  selectedUser,
  socket: socketRef.current,
  video: false,
});


const {
  pendingCall,
  setPendingCall,
} = useCall();



const saveEditMessage = async (messageId) => {
  try {
    const updated = await fetchWithToken(
      `${API_BASE}/api/messages/${messageId}`,
      token,
      {
        method: "PUT",
        body: JSON.stringify({
          text: editText,
        }),
      }
    );

    setMessages((prev) =>
      prev.map((msg) =>
        msg._id === messageId ? updated : msg
      )
    );

    socketRef.current?.emit("message-edited", updated);

    setEditingMessageId(null);
    setEditText("");

  } catch (err) {
    console.log(err);
  }
};



const deleteMessage = async (messageId) => {
  try {
    await fetchWithToken(
      `${API_BASE}/api/messages/${messageId}`,
      token,
      { method: "DELETE" }
    );

    setMessages((prev) =>
      prev.filter((m) => m._id !== messageId)
    );

    socketRef.current?.emit("message-deleted", {
      messageId,
    });
  } catch (err) {
    console.log(err);
  }
};


const deleteForMe = async (messageId) => {
  try {
    await fetchWithToken(
      `${API_BASE}/api/messages/${messageId}`,
      token,
      { method: "DELETE" }
    );

    setMessages((prev) =>
      prev.filter((msg) => msg._id !== messageId)
    );
  } catch (err) {
    console.error("DELETE MESSAGE FOR ME ERROR:", err);
  }
};


const deleteForEveryone = async (messageId) => {

const confirmed = window.confirm(
  "Delete this message for everyone?"
);

if (!confirmed) return;
  try {
    await fetchWithToken(
      `${API_BASE}/api/messages/${messageId}/everyone`,
      token,
      { method: "DELETE" }
    );

    setMessages((prev) =>
      prev.filter((msg) => msg._id !== messageId)
    );

    socketRef.current?.emit("message-deleted", {
      messageId,
    });

  } catch (err) {
    console.log(err);
  }
};


useEffect(() => {
  const handleClickOutside = (e) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(e.target)
    ) {
      setOpenMenuId(null);
    }
  };

  document.addEventListener(
    "mousedown",
    handleClickOutside
  );

  return () =>
    document.removeEventListener(
      "mousedown",
      handleClickOutside
    );
}, []);

  // AUTO SCROLL
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // SOCKET
  useEffect(() => {
    const socket = connectSocket();

    socketRef.current = socket;

    if (!socket) return;

    socket.emit("join", currentUser);

   socket.on("online-users", (users) => {
  setOnlineUsers(users);
});

    socket.on(
  "receive-message",
  (message) => {
    if (
      selectedUser &&
      (
        message.sender === selectedUser._id ||
        message.receiver === selectedUser._id ||
        message.sender?._id === selectedUser._id
      )
    ) {
      setMessages(prev => {
        if (prev.some(msg => msg._id === message._id)) {
          return prev;
        }
        return [...prev, message];
      });
    }
  }
);




socket.on(
  "message-deleted",
  ({ messageId }) => {
    setMessages(prev =>
      prev.filter(
        msg => msg._id !== messageId
      )
    );
  }
);

socket.on(
  "message-edited",
  (updatedMessage) => {
    setMessages(prev =>
      prev.map(msg =>
        msg._id === updatedMessage._id
          ? updatedMessage
          : msg
      )
    );
  }
);

    return () => {
  socket.off("receive-message");
  socket.off("message-deleted");
  socket.off("message-edited");
  socket.off("online-users");
  
};
  }, [
  selectedUser,
  currentUser,
  friends,
]);

  // LOAD MESSAGES
  const loadMessages = async (
    userId
  ) => {
    try {
      const data =
        await fetchWithToken(
          `${API_BASE}/api/messages/${userId}`,
          token
        );

      setMessages(data || []);

      setShowSidebar(false);
    } catch (err) {
      console.log(err);
    }
  };

  

// 👇 Add this here
useEffect(() => {
  if (webRTC.receivingCall) {
    setShowCall(true);
  }
}, [webRTC.receivingCall]);
   

  // FETCH USERS
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data =
          await fetchWithToken(
            `${API_BASE}/api/users`,
            token
          );

        setFriends(data || []);

        // OPEN CHAT FROM URL
        if (id) {
          let foundUser = data.find(
            (u) => u._id === id
          );

          // FETCH USER DIRECTLY
          if (!foundUser) {
            try {
              foundUser =
                await fetchWithToken(
                  `${API_BASE}/api/users/${id}`,
                  token
                );
            } catch (err) {
              console.log(
                "User fetch failed",
                err
              );
            }
          }

          if (foundUser) {
            setSelectedUser(
              foundUser
            );

            loadMessages(
              foundUser._id
            );
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    fetchUsers();
  }, [id]);





    // SEND MESSAGE
  const sendMessage = async () => {
    if (
      !text.trim() &&
      !media
    )
      return;

    try {
      let uploadedMedia = "";
      let mediaType = "";

      // UPLOAD MEDIA
      if (media) {
        setUploading(true);

        const formData =
          new FormData();

        formData.append(
          "file",
          media
        );

        formData.append(
          "upload_preset",
          "joblink_unsigned"
        );

        const cloudName =
          "dycqfqqlc";

        const resourceType =
          media.type.startsWith(
            "video"
          )
            ? "video"
            : media.type.startsWith(
                "image"
              )
            ? "image"
            : "auto";

        const uploadRes =
          await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`,
            {
              method: "POST",
              body: formData,
            }
          );

        const uploadData =
 await uploadRes.json();

console.log(
  "Cloudinary response:",
  uploadData
);

if(uploadData.error){
  throw new Error(
    uploadData.error.message
  );
}

uploadedMedia =
 uploadData.secure_url;

        mediaType =
          media.type.startsWith(
            "video"
          )
            ? "video"
            : media.type.startsWith(
                "audio"
              )
            ? "audio"
            : "image";
      }

      console.log({
        receiver: selectedUser._id,
        text,
        media: uploadedMedia,
        mediaType,
      });

      const newMessage =
        await fetchWithToken(
          `${API_BASE}/api/messages`,
          token,
          {
            method: "POST",
            body: JSON.stringify({
              receiver:
                selectedUser._id,
              text,
              media: uploadedMedia,
              mediaType,
            }),
          }
        );

      console.log(
  "Voice message response:",
  newMessage
);

      setMessages((prev) => [
        ...prev,
        newMessage,
      ]);

      socketRef.current?.emit(
        "send-message",
        newMessage
      );

      setText("");
      setMedia(null);
      setUploading(false);
    } catch (err) {
      console.log(err);

      setUploading(false);
    }
  };

  // ADSTERRA
  useEffect(() => {
    const script =
      document.createElement(
        "script"
      );

    script.src =
      "https://pl29467278.effectivecpmnetwork.com/1ac49ab91139c0ad3e13572497cfbe18/invoke.js";

    script.async = true;

    script.setAttribute(
      "data-cfasync",
      "false"
    );

    document.body.appendChild(
      script
    );

    return () => {
      document.body.removeChild(
        script
      );
    };
  }, []);


const groupedMessages = messages.reduce(
  (groups, message) => {
    const date = formatMessageDate(
      message.createdAt
    );

    if (!groups[date]) {
      groups[date] = [];
    }

    groups[date].push(message);

    return groups;
  },
  {}
);



console.log(
  "First message date:",
  messages[0]?.createdAt
);

console.log(
  "Formatted:",
  formatMessageDate(
    messages[0]?.createdAt
  )
);


    return (
  <div className="flex h-screen overflow-hidden">

      {/* MOBILE OVERLAY */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() =>
            setShowSidebar(false)
          }
        />
      )}

      {/* SIDEBAR */}
      <div
        className={`fixed md:relative z-40 md:z-0 top-0 left-0 h-full w-[320px] bg-white border-r shadow-2xl flex flex-col transform transition-transform duration-300 ${
          showSidebar
            ? "translate-x-0"
            : "-translate-x-full md:translate-x-0"
        }`}
      >

        {/* HEADER */}
        <div className="p-4 border-b flex items-center justify-between bg-white sticky top-0 z-10">
          <h1 className="text-2xl font-bold text-blue-600">
            Messages
          </h1>

          <button
            onClick={() =>
              setShowSidebar(false)
            }
            className="md:hidden text-2xl"
          >
            ✕
          </button>
        </div>

        {/* USERS */}
        <div className="flex-1 overflow-y-auto">
          {friends.map((user) => (
            <motion.div
              whileHover={{
                scale: 1.01,
              }}
              key={user._id}
              onClick={() => {
                setSelectedUser(user);

                loadMessages(
                  user._id
                );
              }}
              className={`flex items-center gap-3 p-4 cursor-pointer transition border-b hover:bg-gray-100 ${
                selectedUser?._id ===
                user._id
                  ? "bg-blue-50"
                  : ""
              }`}
            >
              <div className="relative">
                <img
                  src={
                    user.profilePic ||
                    defaultProfile
                  }
                  alt=""
                  className="w-14 h-14 rounded-full object-cover"
                />

               {onlineUsers.includes(user._id) && (
  <div className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white animate-pulse" />
)}
              </div>

              <div>
                <h2 className="font-semibold text-gray-800">
                  {user.name}
                </h2>

                <p className="text-sm text-gray-500">
                  Tap to chat
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>


<button
  onClick={() => navigate("/")}
  className="
    fixed
    top-[max(env(safe-area-inset-top),16px)]
    right-4
    z-[100]
    w-12
    h-12
    rounded-full
    bg-black/40
    backdrop-blur-md
    border
    border-white/20
    flex
    items-center
    justify-center
    text-white
    transition-all
    active:scale-95
  "
  aria-label="Back to Home"
>
  <ArrowLeft size={24} strokeWidth={2.5} />
</button>



      
      {/* CHAT AREA */}
<div className="relative z-0 flex-1 flex flex-col h-screen overflow-hidden">

        {selectedUser ? (
          <>
            
            {/* HEADER */}
<div className="sticky top-0 z-20 shrink-0 bg-white border-b px-4 py-3 flex items-center justify-between">

              <div className="flex items-center gap-3">

                <button
    onClick={() => setShowSidebar(true)}
    className="md:hidden text-2xl mr-2"
  >
    ☰
  </button>
                  
              

                <img
                  src={
                    selectedUser.profilePic ||
                    defaultProfile
                  }
                  alt=""
                  className="w-12 h-12 rounded-full object-cover"
                />

                <div>
                  <h2 className="font-bold text-lg text-gray-800">
                    {selectedUser.name}
                  </h2>

                 <p
  className={`text-sm ${
    onlineUsers.includes(selectedUser._id)
      ? "text-green-500"
      : "text-gray-400"
  }`}
>
  {onlineUsers.includes(selectedUser._id)
    ? "Online"
    : "Offline"}
</p>


                </div>
              </div>
 
             </div>


            
            {/* Messages */}
<div className="flex-1 overflow-y-auto px-3 py-4">

              {Object.entries(groupedMessages).map(
  ([date, dayMessages]) => (
    <div key={date}>
      <DateDivider date={date} />

      {dayMessages.map(
        (msg, index) => {
                  const isMe =
                    msg.sender ===
                      currentUser ||
                    msg.sender?._id ===
                      currentUser;

 return (
  <motion.div
    key={msg._id}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`flex mb-4 ${
      isMe ? "justify-end" : "justify-start"
    }`}
  >
    <div
      className={`relative max-w-[85%] overflow-visible px-4 py-3 rounded-3xl shadow-md break-words ${
        isMe
          ? "bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-br-md ml-auto"
          : "bg-white text-gray-800 rounded-bl-md mr-auto"
      }`}
    >
      

{/* 3 DOT BUTTON */}             
<div className="relative z-50">
  {isMe && (
    <div className="flex justify-end mb-1">
      <button
        onClick={() =>
          setOpenMenuId(
            openMenuId === msg._id
              ? null
              : msg._id
          )
        }
        className="text-lg"
      >
        ⋮
      </button>
    </div>
  )}


{/* MENU */}
{openMenuId === msg._id && (
  <div
    ref={menuRef}
    className="
      
      absolute
      top-8
      right-0
      bg-black 
      shadow-lg
      rounded-lg
      w-40
      z-50
      border
    "
  >
    <button
      onClick={() => {
        startEditMessage(msg);
        setOpenMenuId(null);
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        text-500-black
        hover:bg-gray-100
        text-sm
      "
    >
      ✏ Edit message
    </button>

    <button
      onClick={() => {
        deleteForMe(msg._id);
        setOpenMenuId(null);
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        text-black-500
        hover:bg-gray-100
        text-sm
      "
    >
      🗑 Delete for me
    </button>

    <button
      onClick={() => {
        deleteForEveryone(msg._id);
        setOpenMenuId(null);
      }}
      className="
        w-full
        text-left
        px-3
        py-2
        text-red-500
        hover:bg-gray-100
        text-sm
      "
    >
      ❌ Delete for everyone
    </button>
  </div>
)}


                        {/* IMAGE */}
                        {msg.mediaType ===
                          "image" && (
                          <img
                            src={msg.media}
                            alt=""
                            className="rounded-2xl mb-2 max-w-full"
                          />
                        )}

                        {/* VIDEO */}
                        {msg.mediaType ===
                          "video" && (
                          <video
                            controls
                            className="rounded-2xl mb-2 max-w-full"
                          >
                            <source
                              src={
                                msg.media
                              }
                            />
                          </video>
                        )}

                        {/* AUDIO */}
                        {msg.mediaType === "audio" && (
                          <AudioMessage
  src={msg.media}
  isMe={isMe}
  onDelete={() => {
    setMessages((prev) =>
      prev.filter(
        (_, i) => i !== index
      )
    );
  }}
/>
                        )}

                        {/* TEXT OR EDIT MODE */}
{editingMessageId === msg._id ? (
  <div className="mt-2 space-y-2">

    <textarea
      value={editText}
      onChange={(e) =>
        setEditText(e.target.value)
      }
      className="w-full p-2 rounded-lg border text-black"
      rows={3}
    />

    <div className="flex gap-2">

      <button
  type="button"
  onClick={() =>
    saveEditMessage(msg._id)
  }
  className="
    bg-green-500
    text-white
    px-3
    py-1
    rounded
  "
>
  Save
</button>

      <button
  type="button"
  onClick={() => {
    setEditingMessageId(null);
    setEditText("");
  }}
  className="
    bg-gray-500
    text-white
    px-3
    py-1
    rounded
  "
>
  Cancel
</button>

    </div>
  </div>
) : (
  msg.text && (
    <div>
  <p>{msg.text}</p>

  {msg.updatedAt &&
   msg.updatedAt !== msg.createdAt && (
    <span className="text-[10px] opacity-70">
      (edited)
    </span>
  )}
</div>
  )
)}


                        {/* TIME */}
<p
  className={`text-[11px] mt-1 ${
    isMe
      ? "text-blue-100"
      : "text-gray-400"
  }`}
>
  {new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}
</p>



                      </div>
                      </div>
                    </motion.div>
                  );
                }
              )}
    </div>
  )
)}

              <div
                ref={messagesEndRef}
              />
            </div>

            
            {/* Input */}
<div className="shrink-0 bg-white border-t px-3 py-3">

              <div className="space-y-2">

                {/* INPUT */}
                <div className="w-full">

                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={text}
                    onChange={(e) =>
                      setText(
                        e.target.value
                      )
                    }
                    onKeyDown={(e) =>
                      e.key ===
                        "Enter" &&
                      sendMessage()
                    }
                    className="flex-1 bg-transparent px-2 py-4 outline-none text-[15px]"
                  />
                </div>

                {/* BUTTONS */}
                <div className="flex items-center justify-between gap-2">

                  <div className="flex items-center gap-2">

                    {/* FILE */}
                    <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 w-14 h-14 rounded-full flex items-center justify-center text-xl shadow">


<span>📎</span>
  <span className="text-[10px] font-bold">Media</span>


                      <input
                        type="file"
                        accept="image/*,video/*"
                        hidden
                        onChange={(e) =>
                          setMedia(
                            e.target
                              .files[0]
                          )
                        }
                      />
                    </label>

                    {/* VOICE NOTE */}
                    <div className="bg-gray-100 rounded-full p-1 shadow">

                      <VoiceRecorder
                        receiverId={
                          selectedUser._id
                        }
                        token={token}
                        onSend={async (
                          audioUrl
                        ) => {
                          try {
                            const newMessage =
                              await fetchWithToken(
                                `${API_BASE}/api/messages`,
                                token,
                                {
                                  method:
                                    "POST",

                                  body:
                                    JSON.stringify(
                                      {
                                        receiver:
                                          selectedUser._id,

                                        media:
                                          audioUrl,

                                        mediaType:
                                          "audio",
                                      }
                                    ),
                                }
                              );

                            setMessages(
                              (
                                prev
                              ) => [
                                ...prev,
                                newMessage,
                              ]
                            );

                            socketRef.current?.emit(
                              "send-message",
                              newMessage
                            );
                          } catch (err) {
                            console.log(
                              err
                            );
                          }
                        }}
                      />
                    </div>
                 

{/* Voice Call */}
<button
  onClick={() => {
setIsOutgoingCall(true);
    setShowCall(true);
    webRTC.startVoiceCall();
  }}
  className="w-14 h-14 rounded-full bg-blue-500 text-white flex items-center justify-center shadow active:scale-95"
>
  <span>📞</span>
  <span className="text-[10px] font-bold">Voice</span>
</button>



{/* Video Call */}
<button
  onClick={() => {
setIsOutgoingCall(true);
    setShowCall(true);
    webRTC.startVideoCall();
  }}
  className="w-14 h-14 rounded-full bg-green-500 text-white flex flex-col items-center justify-center shadow active:scale-95"
>
 <span>📞</span>
  <span className="text-[10px]">Video</span>
</button>
 </div>



                  {/* SEND */}
                  <button
                    onClick={
                      sendMessage
                    }
                    disabled={
                      uploading
                    }
                    className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-full font-semibold shadow-lg"
                  >
                    {uploading
                      ? "..."
                      : "Chat"}
                  </button>
                </div>



                {/* FILE NAME */}
                {media && (
                  <div className="text-xs text-gray-500 truncate px-2">
                    📎 {media.name}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 relative">

            <button
              onClick={() =>
                setShowSidebar(true)
              }
              className="md:hidden absolute top-4 left-4 text-3xl"
            >
              ☰
            </button>

            <div className="text-7xl mb-4">
              💬
            </div>

            <div
              id="container-1ac49ab91139c0ad3e13572497cfbe18"
              className="my-4"
            />

            <div className="text-center px-4">
              <h2 className="text-3xl font-bold text-gray-700">
                Welcome to
                Message Room
              </h2>

              <p className="text-gray-500 mt-2 text-lg">
                Click on the left top menu to select a user to chat with.

              </p>
            </div>
          </div>
        )}
      </div>

      {/* VIDEO CALL */}
      {showCall &&
  selectedUser && (
  <CallScreen
    {...webRTC}
    isOutgoing={isOutgoingCall}
    selectedUser={selectedUser}
    defaultProfile={defaultProfile}
    onClose={() => {
        setShowCall(false);
        setIsOutgoingCall(false);
    }}
/>
)}

   </div>
  );
};

export default Messages;